import {applyOperation, DOCUMENT_FIELDS_PATH, type ConnectionOperation} from '@github-ui/optimistic-connection'
import {useConnectionMutationBridge} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {
  type ConnectionMutationTarget,
  type SetFieldsMapper,
  useMergedConnections,
} from '@github-ui/optimistic-connection/useMergedConnections'
import type {QueryKey} from '@github-ui/react-query'
import {useQueryClient} from '@github-ui/react-query'
import {useCallback, useMemo} from 'react'

import type {IssuesDashboardEnrichedDataPayload} from '../types/routes-types'
import {rowNodeQueryKey} from './issues-dashboard-row-node'

const INACTIVE_OWNER_ID = 'issues-dashboard-no-active-row'

/**
 * Dashboard rows are a denormalized payload that carries the issue's global node id as
 * `globalId` (`id` is the row's own database id). Producers publish only the global id, so
 * this document declares where it stores it — for the read-time overlay and for the
 * confirmed fold, which must match the same rows or confirmed data never lands.
 */
const ISSUE_ROW_IDENTITY_PATH = ['globalId']

type EnrichedRow = IssuesDashboardEnrichedDataPayload['issues'][number]

export const mapIssueViewerFieldsToIssueRow: SetFieldsMapper = operationFields => {
  const fields: Record<string, unknown> = {}
  if (typeof operationFields.title === 'string') fields.title = operationFields.title
  if (typeof operationFields.titleHTML === 'string') fields.titleHtml = operationFields.titleHTML
  if (typeof operationFields.state === 'string') fields.state = operationFields.state
  if (operationFields.stateReason === null || typeof operationFields.stateReason === 'string') {
    fields.stateReason = operationFields.stateReason
  }
  if (typeof operationFields.closed === 'boolean') fields.closed = operationFields.closed
  if (operationFields.closedAt === null || typeof operationFields.closedAt === 'string') {
    fields.closedAt = operationFields.closedAt
  }
  return Object.keys(fields).length > 0 ? fields : undefined
}

function setOperationResult(
  queryClient: ReturnType<typeof useQueryClient>,
  queryKey: QueryKey,
  ownerId: string,
  operation: ConnectionOperation,
) {
  const base = queryClient.getQueryData(queryKey)
  if (base === undefined) return
  const next = applyOperation(base, ownerId, operation, {identityPath: ISSUE_ROW_IDENTITY_PATH})
  if (next !== base) {
    const queryState = queryClient.getQueryState(queryKey)
    queryClient.setQueryData(queryKey, next, queryState ? {updatedAt: queryState.dataUpdatedAt} : undefined)
  }
}

export function useDashboardIssueMutationBridge({
  ownerId: requestedOwnerId,
  rowNodeCacheKey,
  mainQueryKey,
  enabled,
}: {
  ownerId: string | undefined
  rowNodeCacheKey: string | undefined
  mainQueryKey: QueryKey | undefined
  enabled: boolean
}): void {
  const queryClient = useQueryClient()
  const ownerId = requestedOwnerId ?? INACTIVE_OWNER_ID
  const nodeQueryKey = rowNodeCacheKey ? rowNodeQueryKey(rowNodeCacheKey) : undefined

  const onCompleted = useCallback(
    async (operation: ConnectionOperation) => {
      const rowOperation =
        operation.type === 'setFields'
          ? (() => {
              const fields = mapIssueViewerFieldsToIssueRow(operation.fields)
              return fields ? {...operation, fields} : undefined
            })()
          : operation
      if (!rowOperation) return
      const queryKeys = [mainQueryKey, nodeQueryKey].filter((key): key is QueryKey => key !== undefined)
      const mainWasFetching = mainQueryKey ? queryClient.isFetching({queryKey: mainQueryKey}) > 0 : false
      await Promise.all(queryKeys.map(queryKey => queryClient.cancelQueries({queryKey})))
      for (const queryKey of queryKeys) setOperationResult(queryClient, queryKey, ownerId, rowOperation)

      const reconciliation: Array<Promise<unknown>> = []
      if (nodeQueryKey) reconciliation.push(queryClient.invalidateQueries({queryKey: nodeQueryKey}))
      if (mainWasFetching && mainQueryKey) {
        reconciliation.push(queryClient.invalidateQueries({queryKey: mainQueryKey, refetchType: 'all'}))
      }
      await Promise.all(reconciliation)

      // A refetch can race with the mutation response. Reapply the confirmed fields over the
      // refreshed server base before allowing the pending overlay to drop.
      for (const queryKey of queryKeys) setOperationResult(queryClient, queryKey, ownerId, rowOperation)
    },
    [mainQueryKey, nodeQueryKey, ownerId, queryClient],
  )

  const onOutstandingAbandoned = useCallback(() => {
    if (mainQueryKey) void queryClient.invalidateQueries({queryKey: mainQueryKey})
    if (nodeQueryKey) void queryClient.invalidateQueries({queryKey: nodeQueryKey})
  }, [mainQueryKey, nodeQueryKey, queryClient])

  useConnectionMutationBridge({
    ownerId,
    connectionPath: DOCUMENT_FIELDS_PATH,
    enabled,
    onCompleted,
    onOutstandingAbandoned,
  })
}

export function useOptimisticDashboardRows({base, enabled}: {base: EnrichedRow[]; enabled: boolean}): EnrichedRow[] {
  const targets = useMemo<ConnectionMutationTarget[]>(
    () =>
      base.flatMap(row =>
        row.itemType === 'issue' && row.globalId ? [{ownerId: row.globalId, connectionPath: DOCUMENT_FIELDS_PATH}] : [],
      ),
    [base],
  )
  return useMergedConnections({
    base,
    targets,
    enabled,
    mapSetFields: mapIssueViewerFieldsToIssueRow,
    identityPath: ISSUE_ROW_IDENTITY_PATH,
  })
}
